import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { TrainingProgramOverview } from '@/components/training/TrainingProgramOverview';
import { EmptyState } from '@/components/training/EmptyStates';
import { getLatestTrainingPlan } from '@/lib/api/training-program';
import { loadTrainingProgram, type LoadedTrainingProgram } from '@/lib/training-program/program-loader';
import type { TrainingPlan } from '@/types/curriculum';

type TrainingPlanWithSource = TrainingPlan & {
  source_snapshot_id?: string | null;
  source_analysis_run_id?: string | null;
};

export default function TrainingProgramOverviewPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [programData, setProgramData] = useState<LoadedTrainingProgram | null>(null);
  const [planSource, setPlanSource] = useState<TrainingPlanWithSource | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const response = await getLatestTrainingPlan();
      if (!response?.plan?.plan_json) {
        if (!cancelled) {
          setProgramData(null);
          setPlanSource(null);
          setLoading(false);
        }
        return;
      }

      const planJson = response.plan.plan_json as TrainingPlanWithSource;
      const loaded = await loadTrainingProgram(planJson);
      if (!cancelled) {
        setProgramData(loaded);
        setPlanSource(planJson);
        setLoading(false);
      }
    };

    load().catch(() => {
      if (!cancelled) {
        setProgramData(null);
        setPlanSource(null);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const analysisLink = useMemo(() => {
    if (!planSource) return null;
    if (planSource.source_snapshot_id) {
      return `/snapshot/results/${planSource.source_snapshot_id}`;
    }
    if (planSource.source_analysis_run_id) {
      return `/analysis/${planSource.source_analysis_run_id}`;
    }
    return null;
  }, [planSource]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!programData || !programData.program.required_drills.length) {
    return (
      <EmptyState
        type="no-program"
        onAction={() => navigate('/snapshot/select')}
        actionLabel="Create Training Plan from Swing Analysis"
      />
    );
  }

  return (
    <TrainingProgramOverview
      program={programData.program}
      onNavigateToDrill={(drillId) => navigate(`/training-program/drill/${drillId}`)}
      onNavigateToAddOptional={() => navigate('/training-program/optional')}
      onNavigateToStart={(drillId) => navigate(`/training-program/start/${drillId}`)}
      onNavigateToSwingAnalysis={analysisLink ? () => navigate(analysisLink) : undefined}
    />
  );
}
